/** Game-lifecycle analytics tap. Privacy: never forwards names, vote targets, guess text, or word. */
import { type Phase, type ServerMsg, ServerMsgKind } from '@infiltre/shared';
import {
  type DriverMode,
  type DriverStatus,
  DriverStatusKind,
  type GameDriver,
} from '@/shared/lib/driver';
import { trackEvent } from './client';
import type {
  AnalyticsErrorLifecycle,
  AnalyticsHostChangeReason,
  AnalyticsMode,
  AnalyticsReplayStaleCode,
} from './events';

type Tap = {
  unsubscribe: () => void;
  /** Update self player id after identity hydrates post-attach. */
  setSelfId: (id: string | null) => void;
};

type State = {
  mode: DriverMode;
  round: number;
  startedAt: number | null;
  prevStatus: DriverStatusKind;
  reconnectAttempts: number;
  reconnectStartedAt: number | null;
  roomId: string | null;
  selfId: string | null;
  startEmittedForRound: number;
  playerCount: number;
  phase: Phase | null;
  lifecycle: AnalyticsErrorLifecycle;
  /** Lifecycle snapshot captured at reconnect start so we can restore on finish. */
  lifecycleBeforeReconnect: AnalyticsErrorLifecycle | null;
};

const HOST_CHANGE_REASON: Record<string, AnalyticsHostChangeReason> = {
  manual: 'manual',
  'auto-disconnect': 'auto_disconnect',
  'auto-grace-expired': 'auto_grace_expired',
};

/** Codes that fire constantly when the pending queue replays past a phase advance. */
const RACE_BENIGN: ReadonlySet<string> = new Set<AnalyticsReplayStaleCode>([
  'wrong_phase',
  'invalid_target',
  'not_a_member',
  'unknown_player',
]);

const isReplayStaleCode = (code: string): code is AnalyticsReplayStaleCode => RACE_BENIGN.has(code);

const toAnalyticsMode = (mode: DriverMode): AnalyticsMode => mode;

const onRoomState = (
  state: State,
  msg: Extract<ServerMsg, { kind: typeof ServerMsgKind.RoomState }>,
): void => {
  state.roomId = msg.room.id;
  state.playerCount = msg.room.players.length;
  state.phase = msg.room.phase;
  const nextRound = msg.room.round;
  // Single round-transition signal — replaces phase_changed + reach + next_round_requested storm.
  if (state.startedAt !== null && nextRound !== state.round && nextRound > 0) {
    trackEvent({
      name: 'game.round_advanced',
      data: { roomId: state.roomId, round: nextRound, prevRound: state.round },
    });
  }
  state.round = nextRound;
};

const onGameStarted = (state: State): void => {
  state.startedAt = Date.now();
  state.lifecycle = 'steady';
  if (state.startEmittedForRound === state.round) return;
  if (!state.roomId) return;
  const mode = toAnalyticsMode(state.mode);
  trackEvent({
    name: `game.started_${mode}`,
    data: { roomId: state.roomId, gameMode: 'infiltre_classic', playerCount: state.playerCount },
  });
  state.startEmittedForRound = state.round;
};

const onHostChanged = (
  state: State,
  msg: Extract<ServerMsg, { kind: typeof ServerMsgKind.HostChanged }>,
): void => {
  if (!state.roomId) return;
  const reason = HOST_CHANGE_REASON[msg.reason ?? 'manual'] ?? 'manual';
  const isMe = state.selfId !== null && msg.newHostId === state.selfId;
  trackEvent({
    name: `room.host_changed_${reason}`,
    data: { roomId: state.roomId, isMe },
  });
};

const onGameEnded = (
  state: State,
  msg: Extract<ServerMsg, { kind: typeof ServerMsgKind.GameEnded }>,
): void => {
  if (!state.roomId) return;
  const now = Date.now();
  const duration = state.startedAt ? now - state.startedAt : 0;
  const mode = toAnalyticsMode(state.mode);
  trackEvent({
    name: `game.ended_${mode}`,
    data: { roomId: state.roomId, winner: msg.winner, rounds: state.round, durationMs: duration },
  });
  state.startedAt = null;
  state.lifecycle = 'boot';
};

const onError = (
  state: State,
  msg: Extract<ServerMsg, { kind: typeof ServerMsgKind.Error }>,
): void => {
  const phase: Phase | 'unknown' = msg.phase ?? state.phase ?? 'unknown';
  // Reconnect-window stale-replay errors are benign — route to a dedicated
  // event so engine_error keeps signal-to-noise high.
  if (state.lifecycle === 'during_reconnect' && isReplayStaleCode(msg.code)) {
    trackEvent({
      name: `driver.replay_stale_${msg.code}`,
      data: { phase },
    });
    return;
  }
  trackEvent({
    name: 'game.engine_error',
    data: { code: msg.code, phase, lifecycle: state.lifecycle },
  });
};

const dispatchMessage = (state: State, msg: ServerMsg): void => {
  switch (msg.kind) {
    case ServerMsgKind.RoomState:
      onRoomState(state, msg);
      return;
    case ServerMsgKind.GameStarted:
      onGameStarted(state);
      return;
    case ServerMsgKind.HostChanged:
      onHostChanged(state, msg);
      return;
    case ServerMsgKind.GameEnded:
      onGameEnded(state, msg);
      return;
    case ServerMsgKind.Error:
      onError(state, msg);
      return;
    default:
      return;
  }
};

const isReconnectStart = (next: DriverStatusKind, prev: DriverStatusKind): boolean =>
  (next === DriverStatusKind.Connecting || next === DriverStatusKind.Reconnecting) &&
  prev === DriverStatusKind.Ready;

const isReconnectFinish = (next: DriverStatusKind, prev: DriverStatusKind): boolean =>
  next === DriverStatusKind.Ready &&
  (prev === DriverStatusKind.Connecting || prev === DriverStatusKind.Reconnecting);

const beginReconnectWindow = (state: State): void => {
  state.reconnectAttempts += 1;
  if (state.reconnectStartedAt === null) state.reconnectStartedAt = Date.now();
  if (state.lifecycleBeforeReconnect === null) {
    state.lifecycleBeforeReconnect = state.lifecycle;
    state.lifecycle = 'during_reconnect';
  }
};

const endReconnectWindow = (state: State): void => {
  const duration = state.reconnectStartedAt ? Date.now() - state.reconnectStartedAt : 0;
  trackEvent({
    name: 'driver.reconnected',
    data: { attempts: state.reconnectAttempts, durationMs: duration },
  });
  state.reconnectAttempts = 0;
  state.reconnectStartedAt = null;
  if (state.lifecycleBeforeReconnect !== null) {
    state.lifecycle = state.lifecycleBeforeReconnect;
    state.lifecycleBeforeReconnect = null;
  }
};

const dispatchStatus = (state: State, status: DriverStatus): void => {
  if (status.kind === state.prevStatus) return;

  if (isReconnectStart(status.kind, state.prevStatus)) {
    beginReconnectWindow(state);
  }
  if (isReconnectFinish(status.kind, state.prevStatus) && state.reconnectAttempts > 0) {
    endReconnectWindow(state);
  }
  if (status.kind === DriverStatusKind.Unauthorized) {
    trackEvent({
      name: 'driver.auth_rejected_session_bad',
      data: { closeCode: 4401 },
    });
  }
  if (status.kind === DriverStatusKind.KickedDisconnected) {
    trackEvent({ name: 'driver.reconnect_failed_max_attempts', data: {} });
    state.reconnectAttempts = 0;
    state.reconnectStartedAt = null;
  }
  if (status.kind === DriverStatusKind.RoomNotFound && state.reconnectAttempts > 0) {
    trackEvent({ name: 'driver.reconnect_failed_room_gone', data: {} });
    state.reconnectAttempts = 0;
    state.reconnectStartedAt = null;
  }
  state.prevStatus = status.kind;
};

export const attachDriverTap = (driver: GameDriver, selfId: string | null = null): Tap => {
  const state: State = {
    mode: driver.mode,
    round: 0,
    startedAt: null,
    prevStatus: DriverStatusKind.Idle,
    reconnectAttempts: 0,
    reconnectStartedAt: null,
    roomId: null,
    selfId,
    startEmittedForRound: -1,
    playerCount: 0,
    phase: null,
    lifecycle: 'boot',
    lifecycleBeforeReconnect: null,
  };

  const unsubMsg = driver.onMessage((msg) => dispatchMessage(state, msg));
  const unsubStatus = driver.onStatus((status) => dispatchStatus(state, status));

  return {
    unsubscribe: () => {
      unsubMsg();
      unsubStatus();
    },
    setSelfId: (id: string | null) => {
      state.selfId = id;
    },
  };
};
